import {
  flatten,
  range,
  splitEvery,
  pipe,
  append,
  prepend,
  findIndex,
  includes,
  equals,
  complement,
  is,
  isEmpty,
  clamp
} from "ramda";

import { createSelector } from "reselect";

import {
  selectPageControl,
  selectPagesCount
} from "~/store/ducks/domain/config";

import {
  selectRightSidebarActiveTab,
  selectAbsolutePage,
  selectIsStudentActive
} from "~/store/ducks/user/uistate";

import { selectIsDoublePage } from "~/store/ducks/application/page";

const isNotArray = complement(is)(Array);

export const selectIsSinglePageControl = createSelector(
  selectPageControl,
  equals("*single*")
);

export const selectIsDoublePageControl = createSelector(
  selectPageControl,
  equals("*double*")
);

export const selectIsManualPageControl = createSelector(
  selectIsSinglePageControl,
  selectIsDoublePageControl,
  selectPageControl,
  (isSingle, isDouble, pageControl) =>
    !isSingle && !isDouble && (isEmpty(pageControl) || isNotArray(pageControl))
);

export const selectPageIndex = createSelector(
  selectPageControl,
  selectAbsolutePage,
  (pageControl, absolutePage) =>
    findIndex(
      group => equals(absolutePage, group) || includes(absolutePage, group),
      pageControl
    )
);

export const selectHumanReadablePage = null;

export const selectPageFlow = createSelector(
  selectPageControl,
  selectPagesCount,
  selectIsDoublePage,
  selectIsStudentActive,
  selectIsSinglePageControl,
  selectIsDoublePageControl,
  selectIsManualPageControl,
  (
    pageControl,
    pagesCount,
    isDoublePage,
    isStudentActive,
    isSinglePageControl,
    isDoublePageControl,
    isManualPageControl
  ) => {
    const lowestPage = 1;
    const highestPage = pagesCount;
    let groupSize = 0;
    let flow = range(lowestPage + 1, highestPage);

    if (isStudentActive) {
      groupSize = isDoublePage ? 2 : 1;
    } else {
      if (isSinglePageControl) {
        groupSize = 1;
      } else if (isDoublePageControl) {
        groupSize = 2;
      } else if (isManualPageControl) {
        groupSize = isDoublePage ? 2 : 1;
      } else {
        return pageControl;
      }
    }

    const pageFlow = pipe(
      splitEvery(groupSize),
      prepend([lowestPage]),
      append([highestPage])
    )(flow);

    return pageFlow;
  }
);

export const selectPageFlowIndexRange = createSelector(
  selectPageFlow,
  pageFlow => [0, pageFlow.length - 1]
);

export const selectFindPageIndex = page =>
  createSelector(selectPageFlow, selectPagesCount, (pageFlow, pagesCount) => {
    if (page > pagesCount) return pageFlow.length - 1;
    return pageFlow.findIndex(includes(page));
  });

export const selectPageFlowIndex = createSelector(
  selectPageFlow,
  selectAbsolutePage,
  (pageFlow, absolutePage) => pageFlow.findIndex(includes(absolutePage))
);

export const selectNextPageFlowIndex = createSelector(
  selectPageFlowIndexRange,
  selectPageFlowIndex,
  ([minIndex, maxIndex], currentIndex) =>
    clamp(minIndex, maxIndex, currentIndex + 1)
);

export const selectPreviousPageFlowIndex = createSelector(
  selectPageFlowIndexRange,
  selectPageFlowIndex,
  ([minIndex, maxIndex], currentIndex) =>
    clamp(minIndex, maxIndex, currentIndex - 1)
);

export const selectDisplayedPages = createSelector(
  selectPageFlow,
  selectAbsolutePage,
  (pageFlow, absolutePage) => flatten(pageFlow.filter(includes(absolutePage)))
);

export const selectDisplayedPagesOnRightSidebar = createSelector(
  selectDisplayedPages,
  selectRightSidebarActiveTab,
  (pages, activeTab) => (activeTab === "all" ? [] : pages)
);
